// Parked bills → From the floor: bills sent from the phone floor sale today,
// saved on the server as estimates. "Open" puts one on this till's cart (the
// bag must be empty) the same way the till loads a scanned estimate, after
// marking it opened on this till (claimFloorBill) so two tills don't bill it.
import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { getEstimates, upsertEstimate } from "../../../../services";
import useAuthStore from "../../../../stores/useAuthStore";
import useSessionStore from "../../../../stores/useSessionStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { formatMoney } from "../../../../utils/fashion/money";
import { billLineViews } from "../../../../utils/fashion/billLines";
import { bagIsEmpty, cartFromFloorBill, claimFloorBill, listFloorBills, openedElsewhere } from "../../../../utils/fashion/floorEstimates";
import EmptyState from "../EmptyState";

function sentWhen(value) {
  const d = dayjs(value);
  return value && d.isValid() ? `Sent ${d.format("HH:mm")}` : "";
}

function openedWhere(by) {
  const d = dayjs(by?.at);
  return `Opened on ${by?.till || "another till"}${by?.at && d.isValid() ? ` at ${d.format("HH:mm")}` : ""}`;
}

export default function FloorBillsSection({ cart, setCart, tillData, online, customerLabel, variants, onBills, onDone }) {
  const [state, setState] = useState({ loading: true, ok: true, bills: [] });
  const [note, setNote] = useState(null);
  const [opening, setOpening] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const res = await listFloorBills(tillData, getEstimates);
    setState({ loading: false, ok: res.ok, bills: res.bills });
    onBills?.(res.bills);
  }, [tillData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (online) load();
  }, [online, load]);

  const open = async (bill) => {
    if (opening) return;
    if (!bagIsEmpty(cart)) {
      setNote("Finish or park the bill in the bag first.");
      return;
    }
    if (openedElsewhere(bill, tillData) && confirmId !== bill.estimateId) {
      setConfirmId(bill.estimateId);
      setNote(`${bill.openedBy.till || "Another till"} already opened this bill. Tap Open anyway only if that till isn't billing it.`);
      return;
    }
    setOpening(bill.estimateId);
    setNote(null);
    const claim = await claimFloorBill(bill, tillData, { upsertEstimate, getEstimates });
    setOpening(null);
    setConfirmId(null);
    if (!claim.ok) {
      setNote(claim.message);
      if (claim.gone) load();
      return;
    }
    const next = cartFromFloorBill(claim.bill, {
      tillData,
      documentSequence: useSessionStore.getState().documentSequence,
      tillName: useAuthStore.getState().tillValue?.name,
    });
    // Same customer handling as the till's estimate scan.
    if (next?.customer?.isB2bEnabled === "Y") {
      localStorage.setItem("isB2bEnabled", "true");
      useTransactionStore.getState().setIsB2bEnabled("true");
    }
    if (next?.customer?.customerAddressIds) localStorage.setItem("customerAddressIds", JSON.stringify(next.customer.customerAddressIds));
    setCart(next);
    onDone?.();
  };

  let body;
  if (!online) body = <EmptyState compact icon="offline" title="Bills from the floor show here when the till is online." />;
  else if (state.loading && state.bills.length === 0) body = <p className="fp-bills-meta">Looking for bills from the floor…</p>;
  else if (!state.ok) body = <EmptyState compact icon="error" title="Couldn't load bills from the floor. Try Refresh." />;
  else if (state.bills.length === 0) body = <EmptyState compact title="No bills from the floor today." hint="Estimates saved by floor staff show here." />;

  return (
    <section className="fp-bills-floor" aria-label="From the floor">
      <div className="fp-bills-floor-head">
        <b>From the floor</b>
        {online && (
          <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={load} disabled={state.loading}>
            Refresh
          </button>
        )}
      </div>
      {note && (
        <p className="fp-bills-note" role="status">
          {note}
        </p>
      )}
      {body}
      {online &&
        state.bills.map((bill) => {
          const c = bill.cart.customer && typeof bill.cart.customer === "object" ? bill.cart.customer : null;
          const lines = billLineViews(bill.cart.items, variants);
          const qty = bill.cart.totalQty ?? lines.reduce((a, l) => a + Math.abs(l.qty), 0);
          const elsewhere = openedElsewhere(bill, tillData);
          return (
            <article key={bill.estimateId} className="fp-bills-card" aria-label={`Floor bill ${bill.documentno}`}>
              <header className="fp-bills-card-head">
                <span className="fp-bills-card-id">
                  <b className="fp-num">{bill.documentno || "Floor bill"}</b>
                  <span className="fp-bills-meta fp-num">{sentWhen(bill.created)}</span>
                  {elsewhere && <span className="fp-bills-meta">{openedWhere(bill.openedBy)}</span>}
                </span>
                <span className="fp-bills-card-cust">
                  <b>{customerLabel(bill.cart.customer)}</b>
                  {c?.mobileNo && <span className="fp-bills-meta fp-num">{c.mobileNo}</span>}
                </span>
              </header>
              <ul className="fp-bills-lines" aria-label="Items">
                {lines.map((l) => (
                  <li key={l.key} className="fp-bills-line">
                    <span className="fp-bills-line-name">
                      {l.name}
                      {l.variant && <span className="fp-bills-meta">{l.variant}</span>}
                    </span>
                    <span className="fp-num fp-bills-line-qty">× {l.qty}</span>
                    <span className="fp-num fp-bills-line-amt">{formatMoney(l.amount, tillData)}</span>
                  </li>
                ))}
              </ul>
              <footer className="fp-bills-card-foot">
                <span className="fp-bills-total">
                  <span className="fp-bills-meta fp-num">{qty} items</span>
                  <b className="fp-num">{formatMoney(bill.cart.total, tillData)}</b>
                </span>
                <span className="fp-bills-acts">
                  <button type="button" className="fp-bills-pri" onClick={() => open(bill)} disabled={!!opening}>
                    {opening === bill.estimateId ? "Opening…" : confirmId === bill.estimateId ? "Open anyway" : "Open on this till"}
                  </button>
                </span>
              </footer>
            </article>
          );
        })}
    </section>
  );
}
